import React, { type ComponentRef } from 'react'

import { type ToggleGroupSingleProps } from '@radix-ui/react-toggle-group'
import * as ToggleGroup from '@radix-ui/react-toggle-group'
import { cva } from 'class-variance-authority'

import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { AutomationSpecificationsPopup } from '@/components/molecules/AutomationSpecificationsPopup/AutomationSpecificationsPopup.tsx'
import { cn } from '@/lib/utils.ts'

const assignationButtonVariants = cva('flex h-8 w-10 items-center justify-center ', {
  variants: {
    color: {
      orange: '',
      green: '',
    },
    disabled: {
      true: '*:fill-gray-500',
      false: '',
    },
    active: {
      true: '*:fill-white',
      false: '',
    },
  },
  defaultVariants: {
    active: false,
    disabled: false,
  },
  compoundVariants: [
    {
      active: false,
      disabled: false,
      class:
        'border border-gray-600 bg-[linear-gradient(180deg,rgba(58,72,98,0.94)_0%,rgba(17,25,40,0.78)_5.21%,#010203_100%)] *:fill-gray-400 *:hover:fill-gray-200 cursor-pointer',
    },
    {
      color: 'orange',
      active: true,
      class: 'bg-orange-400 cursor-pointer',
    },
    {
      color: 'green',
      active: true,
      class: 'bg-green-400 cursor-pointer',
    },
  ],
})

export interface AssignationStatusProps extends Omit<ToggleGroupSingleProps, 'type' | 'onValueChange'> {
  state: 'manual' | 'solver'
  onValueChange: (state: 'manual' | 'solver') => void
  manualSpecifications: {
    title: string
    description: string
    levelName: string
    items: string[]
  }
  solverSpecifications: {
    title: string
    description: string
    levelName: string
    items: string[]
  }
}

export const AssignationStatus = React.forwardRef<ComponentRef<typeof ToggleGroup.Root>, AssignationStatusProps>(
  (
    { className, state = 'manual', disabled, onValueChange, manualSpecifications, solverSpecifications, ...props },
    ref
  ) => (
    <ToggleGroup.Root
      type="single"
      value={state}
      disabled={disabled}
      className={cn('inline-flex rounded-sm', className)}
      onValueChange={(value) => {
        if (value) {
          onValueChange(value as 'manual' | 'solver')
        }
      }}
      ref={ref}
      {...props}>
      <ToggleGroup.Item value="manual" className="rounded-l-[6px] bg-white disabled:bg-gray-900">
        <AutomationSpecificationsPopup mode="manual" {...manualSpecifications}>
          <div
            className={cn(
              assignationButtonVariants({ active: state === 'manual', color: 'orange', disabled, className }),
              'rounded-l-[6px]'
            )}
            ref={ref}
            {...props}>
            <Icons.manualAssignation className="w-4" />
          </div>
        </AutomationSpecificationsPopup>
      </ToggleGroup.Item>
      <ToggleGroup.Item value="solver" className="rounded-r-[6px] bg-white disabled:bg-gray-900">
        <AutomationSpecificationsPopup mode="solver" {...solverSpecifications}>
          <div
            className={cn(
              assignationButtonVariants({ active: state === 'solver', color: 'green', disabled, className }),
              'rounded-r-[6px]'
            )}
            ref={ref}
            {...props}>
            <Icons.shootingStar className="w-4" />
          </div>
        </AutomationSpecificationsPopup>
      </ToggleGroup.Item>
    </ToggleGroup.Root>
  )
)
